import { useEffect } from "react";
import { useLanguage, SUPPORTED_LOCALES, DEFAULT_LOCALE, withLocalePrefix } from "@/context/LanguageContext";
import useSettings from "@/hooks/useSettings";

/**
 * Inject the per-page SEO tags into <head>.
 *
 *   <PageHead
 *      slug="home"
 *      seo={{ title, description, og_image }}
 *      path="/matelas"            // core path (without locale prefix)
 *   />
 */
export default function PageHead({ seo = {}, path = "/", noindex = false }) {
    const { locale } = useLanguage();
    const settings = useSettings();

    useEffect(() => {
        const title = seo.title || document.title;
        if (title) document.title = title;

        const setMeta = (attr, name, content) => {
            if (!content) return;
            let el = document.head.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                el.setAttribute("data-page-head", "true");
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        setMeta("name", "description", seo.description);
        setMeta("name", "robots", noindex ? "noindex, nofollow, noarchive" : "index, follow");
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", seo.description);
        setMeta("property", "og:locale", { fr: "fr_BE", nl: "nl_BE", en: "en_US" }[locale] || "fr_BE");
        setMeta("property", "og:type", "website");
        if (seo.og_image) setMeta("property", "og:image", seo.og_image);

        // Set <html lang="">
        document.documentElement.setAttribute("lang", locale);

        // Manage canonical + hreflang links (enabled languages only)
        const removeManaged = () => {
            document.head.querySelectorAll('link[data-page-head="true"]').forEach((n) => n.remove());
        };
        removeManaged();

        const base = window.location.origin;
        const enabled = (settings.languages || {});
        const enabledLocales = SUPPORTED_LOCALES.filter((l) => enabled[l] !== false);

        const addLink = (rel, href, hreflang) => {
            const l = document.createElement("link");
            l.setAttribute("rel", rel);
            l.setAttribute("href", href);
            if (hreflang) l.setAttribute("hreflang", hreflang);
            l.setAttribute("data-page-head", "true");
            document.head.appendChild(l);
        };

        const canonical = `${base}${withLocalePrefix(path, locale)}`;
        addLink("canonical", canonical);
        for (const l of enabledLocales) {
            addLink("alternate", `${base}${withLocalePrefix(path, l)}`, l);
        }
        // x-default → French
        if (enabled[DEFAULT_LOCALE] !== false) {
            addLink("alternate", `${base}${withLocalePrefix(path, DEFAULT_LOCALE)}`, "x-default");
        }

        return () => {
            // On unmount don't wipe title — next page will overwrite. Just clean managed links.
            document.head.querySelectorAll('link[data-page-head="true"]').forEach((n) => n.remove());
        };
    }, [seo.title, seo.description, seo.og_image, locale, path, noindex, settings.languages]);

    return null;
}
